import { useQuery } from '@tanstack/react-query';
import { fetchData } from '../../../utils';
import useSmartlook from '../../useSmartlook';

export function useActiveLoanQuery() {
  const { error } = useSmartlook();
  return useQuery<Loan>({
    queryKey: ['active-loan'],
    queryFn: async () => {
      try {
        const data = await fetchData(`/client/loans/latest`);
        return data as Loan;
      } catch (e) {
        error('[useActiveLoanQuery][GET /client/loans/latest]', e);
        throw e;
      }
    },
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
  });
}

export type LoanExtensionFee = {
  term: number;
  unit: string;
  fee: number;
  interest: number;
  feeWithoutInterest: number;
};

type Term = {
  value: number;
  termUnit: string;
};

export type Loan = {
  assignedInterest: number;
  agreementLink: string;
  invoiceLink: string;
  paymentScheduleLink: string;
  loanExtensionFeesLink: string;
  debtPaymentScheduleLink: string;
  latestPaymentLink: string;
  paymentHolidayLink: string;
  fullRepaymentBarcodeLink: string;
  fullRepaymentAmountLink: string;
  extensionRepaymentLink: string;
  paymentsSummary: string;
  term: number;
  id: number;
  signDate: string;
  startDate: string;
  dueDate: string;
  closeDate: string;
  loanNumber: string;
  productCode: string;
  companyMainInfo: {
    name: string;
    registrationNr: string;
  };
  companyBank: {
    accountNumber: string;
    name: string;
    bic: string;
  };
  debtCollectLoanAgreement: boolean;
  renouncementLoanAgreement: boolean;
  overDueDays: number;
  openAmount: number;
  payNowAmount: number;
  payableAmount: number;
  delayedAmountDue: number;
  nonDelayedAmountDue: number;
  penaltyAmount: number;
  commissionAmount: number;
  initialCommissionAmount: number;
  debtPaymentScheduleFee: number;
  schedulePayment: number;
  nearestPaymentDate: string;
  loanExtensionFees: LoanExtensionFee[];
  futureExtensionFees: LoanExtensionFee[];
  autoRepay: boolean;
  amount: number;
  interestAmount: number;
  principalAmount: number;
  totalInvoicedAmount: number;
  totalOutstandingAmount: number;
  annualPercentageRate: number;
  interestRatePerYear: number;
  totalOutpaidAmount: number;
  agreementType: string;
  loanTerm: Term;
  totalTerm: Term;
  mainAgreementSignDate: string;
  gracePeriodInDays: number;
  discounts: {
    type: string;
    percent: number;
    amount: number;
  }[];
  extensionPossible: boolean;
  topUpAmount: number;
};
